// PathwaysAdmin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ----------------------------------------------------------------
 * PathwaysAdmin
 *
 * Backend:
 *   - GET    {backEndUrl}/pathways
 *            → { items: [ { PathwayId, PathwayLabel, Requirements, Enrollments?, UpdatedAt? } ] }
 *
 *            Requirements: [
 *              {
 *                Order,
 *                Title,
 *                MinRequired,
 *                Courses: [{ CoursePk1, EnrollUrl?, Credits? }]
 *              }
 *            ]
 *
 *            Enrollments: [
 *              { UserId }
 *            ]
 *
 *   - PUT    {backEndUrl}/pathways
 *            body: {
 *              PathwayId?,        // optional for new; server can generate
 *              PathwayLabel?,     // optional label
 *              Requirements: [
 *                {
 *                  Order,         // 1-based order of requirements
 *                  Title,
 *                  MinRequired,   // "all" means MinRequired == Courses.length
 *                  Courses: [{ CoursePk1, EnrollUrl?, Credits? }]
 *                }
 *              ],
 *              Enrollments?: [
 *                { UserId }
 *              ]
 *            }
 *
 *   - DELETE {backEndUrl}/pathways
 *            body: { PathwayId }
 *
 * Learn:
 *   - GET /learn/api/public/v3/courses?name={query}
 *   - GET /learn/api/public/v3/courses?courseId={query}
 *   - GET /learn/api/public/v3/courses/{pk1}
 *   - GET /learn/api/public/v1/users?userName={query}
 *   - GET /learn/api/public/v1/users?name={query}
 *   - GET /learn/api/public/v1/users/{pk1}
 *   - GET /learn/api/public/v1/users/me   (for institutionRoleIds)
 * ----------------------------------------------------------------*/

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        gate: {
            checking: "Checking permissions…",
            blockedTitle: "Access Restricted",
            blockedMsg: "The Pathways Admin Tool is not available for your role.",
        },
        headings: {
            main: "Pathways Admin",
            list: "Existing Pathways",
            requirements: "Requirements (ordered pathway steps)",
            enrollments: "Enrollments (students on this pathway)",
            enrollmentsSub: "Students enrolled in this pathway",
            userSearch: "Search Students",
            search: "Search Courses",
            error: "Error",
        },
        buttons: {
            refreshList: "Refresh",
            savePathway: "Save Pathway",
            saved: "Saved",
            addRequirement: "Add Requirement Step",
            removeRequirement: "Remove Requirement",
            search: "Search",
            searching: "Searching…",
            clear: "Clear",
            addToRequirement: "Add to Active Requirement",
            removeCourse: "Remove",
            newPathway: "New Pathway",
            edit: "Edit",
            view: "View",
            delete: "Delete",
            addEnrollment: "Add to Pathway",
            removeEnrollment: "Remove",
        },
        table: {
            pathwayId: "Pathway ID",
            pathwayLabel: "Label",
            steps: "Requirements",
            updatedAt: "Updated",
            name: "Name",
            id: "Course ID",
            availability: "Availability",
            username: "Username",
            credits: "Credits",
            enrollLink: "Enroll link",
            actions: "Actions",
            noResults: "No results",
        },
        pathway: {
            label: "Pathway label",
            labelPlaceholder: "e.g., Nursing - Year 1 Pathway",
            noPathwaySelected:
                "Select a pathway from the list, or create a new one.",
            noRequirements:
                "No requirements yet. Add a requirement step and then add courses to it.",
            requirementLabel: (index) => `Requirement ${index + 1}`,
            requirementRule: "Student must complete at least",
            ofFollowing: "course(s) from this requirement.",
            coursesHeader: "Courses in this requirement",
            noCoursesInRequirement: "No courses in this requirement yet.",
            activeRequirement: "Active requirement for adding courses:",
            noEnrollments: "No students are enrolled in this pathway yet.",
        },
        notices: {
            loading: "Loading PathwaysAdmin…",
            pathwaySaved: "Pathway saved.",
            failedSavePathway: "Failed to save pathway.",
            failedLoadPathways: "Failed to load pathways.",
            deletedPathway: "Pathway deleted.",
            failedDeletePathway: "Failed to delete pathway.",
        },
        search: {
            placeholder:
                "Search by name or courseId (e.g., 'Intro Biology' or QE_TEST_003U)",
            failed: "Search failed.",
        },
        userSearch: {
            placeholder:
                "Search students by name or username (e.g., 'Jane Student' or jstudent1)",
            failed: "User search failed.",
        },
        confirm: {
            deletePathway: "Delete this pathway? This can’t be undone.",
        },
        roleId: "PATHWAYS_ADMIN",
        viewModeBanner: "Viewing pathway details (read-only). Click Edit to make changes.",
        csv: {
            heading: "Bulk CSV upload",
            help: "Headers (case-insensitive): type, requirement, courseId, coursePk1, credits, enrollUrl, userName, userPk1. Use type=course for pathway courses and type=student for enrollments.",
            noPathway: "Select or create a pathway before importing CSV.",
            applied: (courseCount, studentCount) => {
                const parts = [];
                if (courseCount) parts.push(`${courseCount} course row${courseCount === 1 ? "" : "s"}`);
                if (studentCount) parts.push(`${studentCount} student row${studentCount === 1 ? "" : "s"}`);
                return parts.length
                    ? `Applied ${parts.join(" and ")} from CSV.`
                    : "CSV contained no valid rows.";
            },
        },
    },
    en_AU: {},
};

const getMessages = (loc) => ({ ...MESSAGES.en_US, ...(MESSAGES[loc] || {}) });

/** ---------- Learn API adapter ---------- */
function makeLearnApi(platformUrl, token) {
    const base = (platformUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });

    const v1 = "/learn/api/public/v1";
    const v3 = "/learn/api/public/v3";

    async function listCoursesV3(params = {}) {
        const results = [];
        const firstResp = await http.get(`${v3}/courses`, { params });
        const firstBody = firstResp.data;
        const first = Array.isArray(firstBody)
            ? firstBody
            : firstBody?.results || [];
        results.push(...first);

        let next = firstBody?.paging?.nextPage || null;
        while (next) {
            const { data: more } = await http.get(next);
            const chunk = Array.isArray(more) ? more : more?.results || [];
            results.push(...chunk);
            next = more?.paging?.nextPage || null;
        }
        return results;
    }

    async function listUsersV1(params = {}) {
        const results = [];
        const firstResp = await http.get(`${v1}/users`, { params });
        const body = firstResp.data || {};
        const first = Array.isArray(body) ? body : body.results || [];
        results.push(...first);

        let next = body?.paging?.nextPage || null;
        while (next) {
            const { data: more } = await http.get(next);
            const chunk = Array.isArray(more) ? more : more.results || [];
            results.push(...chunk);
            next = more?.paging?.nextPage || null;
        }
        return results;
    }

    async function getCurrentUserInstitutionRoles() {
        const { data } = await http.get(`${v1}/users/me`);
        return data?.institutionRoleIds || [];
    }

    async function getCourseByPk1(pk1) {
        const { data } = await http.get(`${v3}/courses/${pk1}`);
        return data;
    }

    async function getUserByPk1(pk1) {
        const { data } = await http.get(`${v1}/users/${pk1}`);
        return data;
    }

    return {
        searchCoursesByNameV3: (name) => listCoursesV3({ name }),
        searchCoursesByCourseIdV3: (courseId) => listCoursesV3({ courseId }),
        searchUsersByUserName: (userName) => listUsersV1({ userName }),
        searchUsersByName: (name) => listUsersV1({ name }),
        getCurrentUserInstitutionRoles,
        getCourseByPk1,
        getUserByPk1,
    };
}

/** ---------- Backend API adapter ---------- */
function makeBackendApi(backEndUrl) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { Accept: "application/json" },
    });

    return {
        listPathways: async () => {
            const { data } = await http.get(`/pathways`);
            if (Array.isArray(data?.items)) return data.items;
            if (Array.isArray(data)) return data;
            return [];
        },
        savePathway: async (payload) => {
            const { data } = await http.put(`/pathways`, payload);
            return data || {};
        },
        deletePathway: async (pathwayId) => {
            await http.delete(`/pathways`, { data: { PathwayId: pathwayId } });
        },
    };
}

/** ---------- Utilities ---------- */
const uniqBy = (arr, keyFn) => {
    const seen = new Set();
    const out = [];
    for (const item of arr) {
        const k = keyFn(item);
        if (!seen.has(k)) {
            seen.add(k);
            out.push(item);
        }
    }
    return out;
};

function normalizeCourseFromSearch(c) {
    if (!c) return null;
    const pk1 = c.pk1 ?? c.id;
    if (!pk1) return null;

    return {
        id: String(pk1), // ID we store/send is always pk1
        courseId: c.courseId || c.externalId || "",
        name: c.name || c.title || "(untitled course)",
        availability: c?.availability?.available || c?.availability?.availability || "",
        // no enrollUrl from Learn; that is admin-entered only
        // credits not available from Learn; admin enters manually
    };
}

function normalizeCourseFromRequirement(c) {
    if (!c) return null;
    const id = c.CoursePk1 || c.coursePk1 || c.id || c.pk1;
    if (!id) return null;

    const creditsRaw = c.Credits ?? c.credits;
    const credits =
        creditsRaw === undefined || creditsRaw === null
            ? ""
            : String(creditsRaw);

    return {
        id: String(id), // pk1 from backend
        courseId: c.CourseId || c.courseId || c.externalId || "",
        name: c.Name || c.name || c.title || "(untitled course)",
        enrollUrl: c.EnrollUrl || c.enrollUrl || "",
        credits,
    };
}

function normalizeUserFromSearch(u) {
    if (!u) return null;
    const id = u.pk1 ?? u.id ?? u.uuid ?? u.userName;
    const userName = u.userName || u.username || u.externalId || "";
    const nameObj = u.name || {};
    const pieces = [];
    if (nameObj.given) pieces.push(nameObj.given);
    if (nameObj.middle) pieces.push(nameObj.middle);
    if (nameObj.family) pieces.push(nameObj.family);
    const displayName =
        nameObj.displayName ||
        pieces.join(" ") ||
        u.displayName ||
        "";
    if (!id && !userName) return null;
    return {
        id: String(id),       // pk1 if available
        userName: String(userName || ""),
        name: displayName,
    };
}

function normalizeEnrollmentFromItem(e) {
    if (!e) return null;
    const id = e.UserId || e.userId || e.id;
    const userName = e.UserName || e.userName || e.username;
    const name =
        e.Name ||
        e.name ||
        e.displayName ||
        e.fullName ||
        "";
    if (!id && !userName) return null;
    return { id: String(id), userName: String(userName || ""), name: String(name || "") };
}

// ----------- Includes Enrollments -----------
function normalizePathwayItem(item) {
    if (!item) return null;
    const pathwayId =
        item.PathwayId ||
        item.pathwayId ||
        item.Id ||
        item.id ||
        null;

    const label =
        item.PathwayLabel ||
        item.pathwayLabel ||
        item.Label ||
        item.label ||
        "";

    const rawReqs = item.Requirements || item.requirements || [];
    const steps = (rawReqs || []).map((r, index) => {
        const rawCourses = r.Courses || r.courses || [];
        const courses = rawCourses
            .map((c) => normalizeCourseFromRequirement(c))
            .filter(Boolean);
        return {
            id: `step_${index + 1}`,
            title: r.Title || r.title || "",
            minRequired: Number(r.MinRequired || r.minRequired || 1) || 1,
            courses,
        };
    });

    const rawEnrollments = item.Enrollments || item.enrollments || [];
    const enrollments = (rawEnrollments || [])
        .map((e) => normalizeEnrollmentFromItem(e))
        .filter(Boolean);

    const updatedAt = item.UpdatedAt || item.updatedAt || null;

    return {
        pathwayId: pathwayId ? String(pathwayId) : null,
        label,
        steps,
        enrollments,
        updatedAt,
    };
}

// ----------- Serialize (Requirements + Enrollments) -----------
// Only send pk1s + optional EnrollUrl/Credits to the backend; names/usernames are hydrated from Learn on display.
function serializePathway(currentPathway) {
    if (!currentPathway) return null;

    const steps = currentPathway.steps || [];

    const serializedSteps = steps.map((step, index) => {
        const courses = step.courses || [];
        const courseCount = courses.length || 1;
        const rawMin = Number(step.minRequired) || 1;

        const minRequired = Math.min(
            Math.max(1, rawMin),
            courseCount
        );

        return {
            Order: index + 1,
            Title: step.title || `Requirement ${index + 1}`,
            MinRequired: minRequired,
            Courses: courses.map((c) => {
                const coursePayload = {
                    CoursePk1: c.id, // store only pk1
                };

                if (c.enrollUrl) {
                    coursePayload.EnrollUrl = c.enrollUrl;
                }

                if (
                    c.credits !== undefined &&
                    c.credits !== null &&
                    String(c.credits).trim() !== "" &&
                    !Number.isNaN(Number(c.credits))
                ) {
                    coursePayload.Credits = Number(c.credits);
                }

                return coursePayload;
            }),
        };
    });

    const serializedEnrollments = (currentPathway.enrollments || []).map((e) => ({
        UserId: e.id || e.userId || "", // store only pk1
    }));

    return {
        PathwayId: currentPathway.pathwayId || undefined,
        PathwayLabel: currentPathway.label || "",
        Requirements: serializedSteps,
        Enrollments: serializedEnrollments,
    };
}

/** ---- CSV utilities ---- */

// Normalise header names from the CSV into canonical keys
function canonicalHeader(h) {
    const key = (h || "").trim().toLowerCase();
    if (["type", "rowtype"].includes(key)) return "type";
    if (["action", "op"].includes(key)) return "action";
    if (["requirement", "step", "req", "requirementnumber"].includes(key)) return "requirement";
    if (["coursepk1", "course_pk1", "coursepk", "pk1"].includes(key)) return "coursePk1";
    if (["courseid", "course_id"].includes(key)) return "courseId";
    if (["credits", "credit"].includes(key)) return "credits";
    if (["enrollurl", "enrolurl", "enroll_url"].includes(key)) return "enrollUrl";
    if (["userid", "userpk1", "user_pk1", "userpk"].includes(key)) return "userPk1";
    if (["username", "user_name"].includes(key)) return "userName";
    return null;
}

// Very simple CSV parser (no embedded commas/quotes)
function parseCsvFlexible(text) {
    const lines = (text || "")
        .split(/\r?\n/)
        .filter((l) => l.trim() !== "");
    if (!lines.length) return [];

    const headersRaw = lines[0].split(",").map((h) => h.trim());
    const headerKeys = headersRaw.map(canonicalHeader);

    const rows = [];
    for (let i = 1; i < lines.length; i += 1) {
        const cols = lines[i].split(",");
        const row = {};
        headerKeys.forEach((key, idx) => {
            if (!key) return;
            row[key] = (cols[idx] || "").trim();
        });
        if (Object.keys(row).length > 0) {
            row.__rowNumber = i + 1; // keep 1-based line number for messages
            rows.push(row);
        }
    }
    return rows;
}

function detectCsvRowType(row) {
    const t = (row.type || "").trim().toLowerCase();
    if (["course", "c", "class"].includes(t)) return "course";
    if (["student", "user", "enrollment", "enrolment", "s"].includes(t)) return "student";

    // Heuristics if `type` is omitted
    if (row.courseId || row.coursePk1) return "course";
    if (row.userName || row.userPk1) return "student";
    return null;
}

/** ---------- Component ---------- */
const PathwaysAdmin = ({ platformUrl, backEndUrl, locale = "en_US" }) => {
    const M = useMemo(() => getMessages(locale), [locale]);

    const location = useLocation();
    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );
    const token = queryParams.get("token") || "";

    const learn = useMemo(
        () => makeLearnApi(platformUrl, token),
        [platformUrl, token]
    );
    const backend = useMemo(
        () => makeBackendApi(backEndUrl),
        [backEndUrl]
    );

    // Gate
    const [roleChecked, setRoleChecked] = useState(false);
    const [hasAdminRole, setHasAdminRole] = useState(false);

    // Global-ish state
    const [loading, setLoading] = useState(true);
    const [fatalError, setFatalError] = useState("");
    const [inlineError, setInlineError] = useState("");
    const [inlineSuccess, setInlineSuccess] = useState("");
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [viewMode, setViewMode] = useState(false); // true when opened via View

    // CSV state
    const [csvBusy, setCsvBusy] = useState(false);
    const [csvError, setCsvError] = useState("");
    const [csvInfo, setCsvInfo] = useState("");

    // Pathways list + current editor
    // [{ pathwayId, label, steps, enrollments, updatedAt }]
    const [pathways, setPathways] = useState([]);
    const [currentPathway, setCurrentPathway] = useState(null);
    const [activeStepId, setActiveStepId] = useState(null);
    const nextStepIdRef = useRef(1);

    // Course search state
    const [searchTerm, setSearchTerm] = useState("");
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searchError, setSearchError] = useState("");

    // User search (for enrollments)
    const [userSearchTerm, setUserSearchTerm] = useState("");
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [userSearchError, setUserSearchError] = useState("");

    // Compact UI
    const compactCss = `
  .bb-container { font-size: 13px; }
  .bb-card h2 { font-size: 16px; font-weight: 700 !important; }

  .bb-table { font-size: 12px; border-collapse: collapse; width: 100%; }
  .bb-table th, .bb-table td { padding: 6px 8px; border-bottom: 1px solid #eee; }
  .bb-table th { font-weight: 600; text-align: left; }

  .bb-card .bb-button,
  .bb-container .bb-button {
    -webkit-appearance: none !important;
    appearance: none !important;
    background: #f6f6f6 !important;
    border: 1px solid #d3d3d3 !important;
    color: #222 !important;
    border-radius: 6px !important;
    padding: 6px 10px !important;
    font: inherit !important;
    line-height: 1.2 !important;
    text-shadow: none !important;
    box-shadow: none !important;
    cursor: pointer !important;
  }
  .bb-card .bb-button:hover,
  .bb-container .bb-button:hover { background: #efefef !important; }
  .bb-card .bb-button:focus-visible { outline: 2px solid #5a9cff !important; outline-offset: 2px !important; }

  .bb-card .bb-button.danger { background: #fff1f1 !important; border-color: #e6b3b3 !important; color: #7a0a0a !important; }
  .bb-card .bb-button.danger:hover { background: #fee !important; }
  .bb-card .bb-button.secondary { background: #f2f2f2 !important; border-color: #d0d0d0 !important; color: #222 !important; }

  select, input { font-size: 12px; padding: 4px 6px; height: 30px; font-weight: 400; color: inherit; }
  .bb-select { font-size: 12px; height: 30px; padding: 2px 6px; font-weight: 400; color: inherit; }

  .bb-row { display: flex; gap: 8px; align-items: center; }
`;

    const Flash = ({ type, children }) => {
        if (!children) return null;
        const styles = {
            marginBottom: 6,
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid",
            background: type === "error" ? "#ffecec" : "#ecfff1",
            borderColor: type === "error" ? "#ffb3b3" : "#b3ffcc",
            color: type === "error" ? "#a40000" : "#005c2e",
            fontSize: 12,
        };
        return (
            <div
                role={type === "error" ? "alert" : "status"}
                style={styles}
                aria-live="polite"
            >
                {children}
            </div>
        );
    };

    /** ---------------- Role gate ---------------- */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!platformUrl || !token) {
                    if (!cancelled) {
                        setHasAdminRole(false);
                        setRoleChecked(true);
                    }
                    return;
                }
                const roles = await learn.getCurrentUserInstitutionRoles();
                if (!cancelled) {
                    setHasAdminRole(
                        Array.isArray(roles) && roles.includes(M.roleId)
                    );
                    setRoleChecked(true);
                }
            } catch {
                if (!cancelled) {
                    setHasAdminRole(false);
                    setRoleChecked(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [learn, platformUrl, token, M.roleId]);

    /** ---------------- Hydration from Learn (pk1 → details) ---------------- */
    async function hydratePathwaysFromLearn(normalizedPathways) {
        if (!normalizedPathways || normalizedPathways.length === 0) {
            return normalizedPathways;
        }

        try {
            // Collect unique course pk1s and user pk1s
            const courseIdSet = new Set();
            const userIdSet = new Set();

            for (const p of normalizedPathways) {
                (p.steps || []).forEach((step) => {
                    (step.courses || []).forEach((c) => {
                        if (c?.id) courseIdSet.add(String(c.id));
                    });
                });
                (p.enrollments || []).forEach((e) => {
                    if (e?.id) userIdSet.add(String(e.id));
                });
            }

            const courseIds = Array.from(courseIdSet);
            const userIds = Array.from(userIdSet);

            const [courses, users] = await Promise.all([
                Promise.all(
                    courseIds.map(async (id) => {
                        try {
                            const data = await learn.getCourseByPk1(id);
                            return normalizeCourseFromSearch(data);
                        } catch (e) {
                            // Fallback: keep pk1 only
                            console.warn("[PathwaysAdmin] Failed to hydrate course", id, e);
                            return {
                                id: String(id),
                                courseId: "",
                                name: "",
                                availability: "",
                            };
                        }
                    })
                ),
                Promise.all(
                    userIds.map(async (id) => {
                        try {
                            const data = await learn.getUserByPk1(id);
                            return normalizeUserFromSearch(data);
                        } catch (e) {
                            console.warn("[PathwaysAdmin] Failed to hydrate user", id, e);
                            return { id: String(id), userName: "", name: "" };
                        }
                    })
                ),
            ]);

            const courseMap = new Map();
            for (const c of courses) {
                if (c && c.id) courseMap.set(String(c.id), c);
            }

            const userMap = new Map();
            for (const u of users) {
                if (u && u.id) userMap.set(String(u.id), u);
            }

            // Rebuild pathways with hydrated fields, preserving enrollUrl + credits
            const hydrated = normalizedPathways.map((p) => {
                const steps = (p.steps || []).map((step) => ({
                    ...step,
                    courses: (step.courses || []).map((c) => {
                        const full = courseMap.get(String(c.id));
                        if (full) {
                            return {
                                id: full.id,
                                courseId: full.courseId,
                                name: full.name,
                                availability: full.availability,
                                enrollUrl: c.enrollUrl || "",
                                credits: c.credits ?? "",
                            };
                        }
                        return {
                            ...c,
                            credits: c.credits ?? "",
                        };
                    }),
                }));

                const enrollments = (p.enrollments || []).map((e) => {
                    const full = userMap.get(String(e.id));
                    return full
                        ? {
                            id: full.id,
                            userName: full.userName,
                            name: full.name,
                        }
                        : e;
                });

                return {
                    ...p,
                    steps,
                    enrollments,
                };
            });

            return hydrated;
        } catch (e) {
            console.warn("[PathwaysAdmin] Hydration failed, using raw pathways", e);
            return normalizedPathways;
        }
    }

    /** ---------------- Load pathways for tenant ---------------- */
    async function loadPathways({ clearMessages = false, suppressSpinner = false } = {}) {
        if (!suppressSpinner) {
            setLoading(true);
        }
        setFatalError("");
        if (clearMessages) {
            setInlineError("");
            setInlineSuccess("");
        }

        try {
            const items = await backend.listPathways();
            const normalized = (items || [])
                .map((it) => normalizePathwayItem(it))
                .filter(Boolean);

            const hydrated = await hydratePathwaysFromLearn(normalized);

            setPathways(hydrated);

            // Keep editing selection if possible
            setCurrentPathway((prev) => {
                if (!prev || !prev.pathwayId) return prev;
                const match = hydrated.find(
                    (p) => p.pathwayId === prev.pathwayId
                );
                if (match) {
                    nextStepIdRef.current = (match.steps?.length || 0) + 1;
                    return match;
                }
                return prev;
            });
        } catch (err) {
            setFatalError(
                err?.response?.data?.message ||
                err.message ||
                M.notices.failedLoadPathways
            );
        } finally {
            if (!suppressSpinner) {
                setLoading(false);
            }
        }
    }

    // Initial load after gate
    useEffect(() => {
        if (roleChecked && hasAdminRole) {
            loadPathways({ clearMessages: true, suppressSpinner: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleChecked, hasAdminRole, backEndUrl, platformUrl, token]);

    /** ---------------- Editor helpers ---------------- */
    function startNewPathway() {
        setCurrentPathway({
            pathwayId: null,
            label: "",
            steps: [],
            enrollments: [],
            updatedAt: null,
        });
        nextStepIdRef.current = 1;
        setActiveStepId(null);
        setDirty(false);
        setInlineError("");
        setInlineSuccess("");
        setViewMode(false);
    }

    function editExistingPathway(pathwayId) {
        const p = pathways.find((x) => x.pathwayId === pathwayId);
        if (!p) return;
        setCurrentPathway(p);
        setActiveStepId(p.steps?.[0]?.id || null);
        nextStepIdRef.current = (p.steps?.length || 0) + 1;
        setDirty(false);
        setInlineError("");
        setInlineSuccess("");
        setViewMode(false);
    }

    // View-only, no editing
    function viewPathway(pathwayId) {
        const p = pathways.find((x) => x.pathwayId === pathwayId);
        if (!p) return;
        setCurrentPathway(p);
        setActiveStepId(p.steps?.[0]?.id || null);
        nextStepIdRef.current = (p.steps?.length || 0) + 1;
        setDirty(false);
        setInlineError("");
        setInlineSuccess("");
        setViewMode(true);
    }

    function updateLabel(newLabel) {
        if (viewMode) return;
        setCurrentPathway((prev) => {
            if (!prev) {
                return {
                    pathwayId: null,
                    label: newLabel,
                    steps: [],
                    enrollments: [],
                    updatedAt: null,
                };
            }
            return { ...prev, label: newLabel };
        });
        setDirty(true);
    }

    /** ---------------- Requirement editing ---------------- */
    function addRequirementStep() {
        if (viewMode) return;
        const newId = `step_${nextStepIdRef.current++}`;
        setCurrentPathway((prev) => {
            const base =
                prev || {
                    pathwayId: null,
                    label: "",
                    steps: [],
                    enrollments: [],
                    updatedAt: null,
                };
            const steps = [...(base.steps || []), {
                id: newId,
                title: "",
                minRequired: 1,
                courses: [],
            }];
            return { ...base, steps };
        });
        setActiveStepId(newId);
        setDirty(true);
    }

    function removeRequirementStep(stepId) {
        if (viewMode) return;
        setCurrentPathway((prev) => {
            if (!prev) return prev;
            const steps = (prev.steps || []).filter((s) => s.id !== stepId);
            let newActive = activeStepId;
            if (activeStepId === stepId) {
                newActive = steps[0]?.id || null;
            }
            setActiveStepId(newActive);
            return { ...prev, steps };
        });
        setDirty(true);
    }

    function updateStepTitle(stepId, title) {
        if (viewMode) return;
        setCurrentPathway((prev) => {
            if (!prev) return prev;
            const steps = (prev.steps || []).map((s) =>
                s.id === stepId ? { ...s, title } : s
            );
            return { ...prev, steps };
        });
        setDirty(true);
    }

    function updateStepMinRequired(stepId, value) {
        if (viewMode) return;
        const n = Math.max(1, Number(value) || 1);
        setCurrentPathway((prev) => {
            if (!prev) return prev;
            const steps = (prev.steps || []).map((s) =>
                s.id === stepId ? { ...s, minRequired: n } : s
            );
            return { ...prev, steps };
        });
        setDirty(true);
    }

    function removeCourseFromStep(stepId, coursePk1) {
        if (viewMode) return;
        setCurrentPathway((prev) => {
            if (!prev) return prev;
            const steps = (prev.steps || []).map((s) =>
                s.id === stepId
                    ? {
                        ...s,
                        courses: (s.courses || []).filter(
                            (c) => c.id !== coursePk1
                        ),
                    }
                    : s
            );
            return { ...prev, steps };
        });
        setDirty(true);
    }

    function addCourseToActiveRequirement(course) {
        if (viewMode) return;
        const normalized = normalizeCourseFromSearch(course);
        if (!normalized) return;

        setCurrentPathway((prev) => {
            const base =
                prev || {
                    pathwayId: null,
                    label: "",
                    steps: [],
                    enrollments: [],
                    updatedAt: null,
                };

            // ensure new course gets no credits by default
            const newCourse = { ...normalized, enrollUrl: "", credits: "" };

            let stepId = activeStepId;
            let steps = base.steps || [];

            if (!stepId && steps.length === 0) {
                const newId = `step_${nextStepIdRef.current++}`;
                stepId = newId;
                steps = [
                    {
                        id: newId,
                        title: "",
                        minRequired: 1,
                        courses: [newCourse],
                    },
                ];
                setActiveStepId(newId);
            } else if (!stepId && steps.length > 0) {
                stepId = steps[0].id;
                setActiveStepId(stepId);
                steps = steps.map((s) => {
                    if (s.id !== stepId) return s;
                    const exists = (s.courses || []).some(
                        (c) => c.id === newCourse.id
                    );
                    if (exists) return s;
                    return {
                        ...s,
                        courses: [...(s.courses || []), newCourse],
                    };
                });
            } else {
                steps = steps.map((s) => {
                    if (s.id !== stepId) return s;
                    const exists = (s.courses || []).some(
                        (c) => c.id === newCourse.id
                    );
                    if (exists) return s;
                    return {
                        ...s,
                        courses: [...(s.courses || []), newCourse],
                    };
                });
            }

            return { ...base, steps };
        });
        setDirty(true);
    }

    // Update per-course enroll link
    function updateCourseEnrollUrl(stepId, coursePk1, url) {
        if (viewMode) return;
        setCurrentPathway((prev) => {
            if (!prev) return prev;
            const steps = (prev.steps || []).map((s) => {
                if (s.id !== stepId) return s;
                return {
                    ...s,
                    courses: (s.courses || []).map((c) =>
                        c.id === coursePk1 ? { ...c, enrollUrl: url } : c
                    ),
                };
            });
            return { ...prev, steps };
        });
        setDirty(true);
    }

    // update per-course credits
    function updateCourseCredits(stepId, coursePk1, value) {
        if (viewMode) return;
        setCurrentPathway((prev) => {
            if (!prev) return prev;
            const steps = (prev.steps || []).map((s) => {
                if (s.id !== stepId) return s;
                return {
                    ...s,
                    courses: (s.courses || []).map((c) =>
                        c.id === coursePk1
                            ? { ...c, credits: value }
                            : c
                    ),
                };
            });
            return { ...prev, steps };
        });
        setDirty(true);
    }

    /** ---------------- Enrollment editing ---------------- */
    function addStudentToPathway(user) {
        if (viewMode) return;
        const normalized = normalizeUserFromSearch(user);
        if (!normalized) return;

        setCurrentPathway((prev) => {
            const base =
                prev || {
                    pathwayId: null,
                    label: "",
                    steps: [],
                    enrollments: [],
                    updatedAt: null,
                };

            const existing = base.enrollments || [];
            const already = existing.some(
                (e) =>
                    e.id === normalized.id ||
                    e.userName === normalized.userName
            );
            if (already) return base;

            return {
                ...base,
                enrollments: [
                    ...existing,
                    {
                        id: normalized.id,         // pk1
                        userName: normalized.userName,
                        name: normalized.name,
                    },
                ],
            };
        });
        setDirty(true);
    }

    function removeEnrollment(userKey) {
        if (viewMode) return;
        setCurrentPathway((prev) => {
            if (!prev) return prev;
            const prevList = prev.enrollments || [];
            const next = prevList.filter(
                (e) =>
                    e.id !== userKey &&
                    e.userName !== userKey
            );
            return { ...prev, enrollments: next };
        });
        setDirty(true);
    }

    /** ---------------- Save / Delete ---------------- */
    async function saveCurrentPathway() {
        if (viewMode) return; // read-only
        setInlineError("");
        setInlineSuccess("");

        if (!currentPathway) {
            setInlineError(M.pathway.noPathwaySelected);
            return;
        }

        const payload = serializePathway(currentPathway);
        if (!payload) {
            setInlineError(M.notices.failedSavePathway);
            return;
        }

        setSaving(true);
        try {
            const data = await backend.savePathway(payload);
            const newId =
                data?.key?.PathwayId ||
                data?.key?.pathwayId ||
                currentPathway.pathwayId ||
                null;

            if (newId) {
                setCurrentPathway((prev) =>
                    prev ? { ...prev, pathwayId: String(newId) } : prev
                );
            }

            setInlineSuccess(M.notices.pathwaySaved);
            setDirty(false);

            // Refresh list without global spinner
            await loadPathways({ suppressSpinner: true });
        } catch (err) {
            setInlineError(
                err?.response?.data?.message ||
                err.message ||
                M.notices.failedSavePathway
            );
        } finally {
            setSaving(false);
        }
    }

    async function deletePathway(pathwayId) {
        const ok = window.confirm(M.confirm.deletePathway);
        if (!ok) return;

        setInlineError("");
        setInlineSuccess("");

        try {
            await backend.deletePathway(pathwayId);
            setPathways((prev) =>
                prev.filter((p) => p.pathwayId !== pathwayId)
            );
            setInlineSuccess(M.notices.deletedPathway);

            if (
                currentPathway &&
                currentPathway.pathwayId === pathwayId
            ) {
                setCurrentPathway(null);
                setActiveStepId(null);
                setDirty(false);
                setViewMode(false);
            }
        } catch (err) {
            setInlineError(
                err?.response?.data?.message ||
                err.message ||
                M.notices.failedDeletePathway
            );
        }
    }

    /** ---------------- Course search ---------------- */
    async function doSearch() {
        const term = (searchTerm || "").trim();
        if (!term) {
            setSearchResults([]);
            setSearchError("");
            return;
        }
        setSearchLoading(true);
        setSearchError("");
        setInlineError("");
        setInlineSuccess("");

        try {
            const [byName, byCourseId] = await Promise.allSettled([
                learn.searchCoursesByNameV3(term),
                learn.searchCoursesByCourseIdV3(term),
            ]);
            const arr1 = byName.status === "fulfilled" ? byName.value : [];
            const arr2 = byCourseId.status === "fulfilled" ? byCourseId.value : [];

            const merged = uniqBy(
                [...arr1, ...arr2],
                (x) => x && (x.pk1 || x.id)
            )
                .map(normalizeCourseFromSearch)
                .filter(Boolean);

            setSearchResults(merged);
            if (merged.length === 0) {
                const errMsg =
                    byName.status === "rejected" &&
                    byCourseId.status === "rejected"
                        ? byName.reason?.message ||
                        byCourseId.reason?.message ||
                        M.search.failed
                        : M.table.noResults;
                setSearchError(errMsg);
            }
        } catch (err) {
            setSearchError(
                err?.response?.data?.message || err.message || M.search.failed
            );
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    }

    /** ---------------- User search (for enrollments) ---------------- */
    async function doUserSearch() {
        const term = (userSearchTerm || "").trim();
        if (!term) {
            setUserSearchResults([]);
            setUserSearchError("");
            return;
        }

        setUserSearchLoading(true);
        setUserSearchError("");
        setInlineError("");
        setInlineSuccess("");

        try {
            const [byUserName, byName] = await Promise.allSettled([
                learn.searchUsersByUserName(term),
                learn.searchUsersByName(term),
            ]);

            const arr1 = byUserName.status === "fulfilled" ? byUserName.value : [];
            const arr2 = byName.status === "fulfilled" ? byName.value : [];

            const merged = uniqBy(
                [...arr1, ...arr2],
                (x) => x && (x.pk1 || x.id || x.userName)
            )
                .map(normalizeUserFromSearch)
                .filter(Boolean);

            setUserSearchResults(merged);
            if (merged.length === 0) {
                const errMsg =
                    byUserName.status === "rejected" &&
                    byName.status === "rejected"
                        ? byUserName.reason?.message ||
                        byName.reason?.message ||
                        M.userSearch.failed
                        : M.table.noResults;
                setUserSearchError(errMsg);
            }
        } catch (err) {
            setUserSearchError(
                err?.response?.data?.message ||
                err.message ||
                M.userSearch.failed
            );
            setUserSearchResults([]);
        } finally {
            setUserSearchLoading(false);
        }
    }

    /** ---------------- CSV apply helpers ---------------- */
    async function applyCsvText(text) {
        if (!currentPathway) {
            setCsvError(M.csv.noPathway);
            return;
        }

        const rows = parseCsvFlexible(text);
        if (!rows.length) {
            setCsvError("CSV appears to be empty.");
            return;
        }

        const errors = [];
        const courseRows = [];
        const studentRows = [];

        // classify rows & basic validation
        rows.forEach((row) => {
            const rowType = detectCsvRowType(row);
            const rowNum = row.__rowNumber || "?";

            if (!rowType) {
                errors.push(`Row ${rowNum}: could not determine type (course vs student).`);
                return;
            }

            if (rowType === "course") {
                const requirementRaw = row.requirement || "";
                const requirementNumber = Number.parseInt(requirementRaw, 10);
                if (!Number.isFinite(requirementNumber) || requirementNumber < 1) {
                    errors.push(`Row ${rowNum}: requirement must be a positive integer.`);
                    return;
                }

                const coursePk1 = (row.coursePk1 || "").trim();
                const courseId = (row.courseId || "").trim();
                if (!coursePk1 && !courseId) {
                    errors.push(
                        `Row ${rowNum}: course row must include courseId or coursePk1.`
                    );
                    return;
                }

                courseRows.push({
                    rowNum,
                    requirementNumber,
                    coursePk1,
                    courseId,
                    credits: row.credits,
                    enrollUrl: row.enrollUrl || "",
                });
            } else if (rowType === "student") {
                const userPk1 = (row.userPk1 || "").trim();
                const userName = (row.userName || "").trim();
                if (!userPk1 && !userName) {
                    errors.push(
                        `Row ${rowNum}: student row must include userName or userPk1.`
                    );
                    return;
                }

                studentRows.push({
                    rowNum,
                    userPk1,
                    userName,
                });
            }
        });

        // build maps for lookups
        const courseIdMap = new Map(); // lower(courseId) -> original courseId
        courseRows.forEach((r) => {
            if (!r.coursePk1 && r.courseId) {
                const key = r.courseId.toLowerCase();
                if (!courseIdMap.has(key)) courseIdMap.set(key, r.courseId);
            }
        });

        const userNameMap = new Map(); // lower(username) -> original username
        studentRows.forEach((r) => {
            if (!r.userPk1 && r.userName) {
                const key = r.userName.toLowerCase();
                if (!userNameMap.has(key)) userNameMap.set(key, r.userName);
            }
        });

        // Resolve courseIds → pk1 using same Learn search as UI
        const courseLookup = new Map(); // lower(courseId) -> normalized course
        await Promise.all(
            Array.from(courseIdMap.entries()).map(async ([key, courseId]) => {
                try {
                    const results = await learn.searchCoursesByCourseIdV3(courseId);
                    const first = Array.isArray(results) && results.length > 0 ? results[0] : null;
                    if (!first) {
                        errors.push(`Could not find course with courseId "${courseId}".`);
                        return;
                    }
                    const normalized = normalizeCourseFromSearch(first);
                    if (!normalized || !normalized.id) {
                        errors.push(
                            `Course lookup for "${courseId}" did not return a usable course.`
                        );
                        return;
                    }
                    courseLookup.set(key, normalized);
                } catch (e) {
                    errors.push(
                        `Error searching courseId "${courseId}": ${
                            e?.message || String(e)
                        }`
                    );
                }
            })
        );

        // Resolve userName → pk1 using same Learn search as UI
        const userLookup = new Map(); // lower(username) -> normalized user
        await Promise.all(
            Array.from(userNameMap.entries()).map(async ([key, userName]) => {
                try {
                    const results = await learn.searchUsersByUserName(userName);
                    const first =
                        Array.isArray(results) && results.length > 0 ? results[0] : null;
                    if (!first) {
                        errors.push(`Could not find user with username "${userName}".`);
                        return;
                    }
                    const normalized = normalizeUserFromSearch(first);
                    if (!normalized || !normalized.id) {
                        errors.push(
                            `User lookup for "${userName}" did not return a usable user.`
                        );
                        return;
                    }
                    userLookup.set(key, normalized);
                } catch (e) {
                    errors.push(
                        `Error searching username "${userName}": ${
                            e?.message || String(e)
                        }`
                    );
                }
            })
        );

        const base = currentPathway;
        const steps = (base.steps || []).map((step) => ({
            ...step,
            courses: [...(step.courses || [])],
        }));
        const enrollments = [...(base.enrollments || [])];

        let addedCourses = 0;
        let addedStudents = 0;

        // apply course rows
        courseRows.forEach((r) => {
            const stepIndex = r.requirementNumber - 1;
            const rowLabel = `row ${r.rowNum}`;

            if (!steps[stepIndex]) {
                errors.push(
                    `${rowLabel}: requirement ${r.requirementNumber} does not exist on this pathway.`
                );
                return;
            }

            let normalizedCourse = null;
            let coursePk1 = (r.coursePk1 || "").trim();
            const courseIdLower = r.courseId ? r.courseId.toLowerCase() : "";

            if (!coursePk1 && courseIdLower) {
                const found = courseLookup.get(courseIdLower);
                if (found) {
                    normalizedCourse = found;
                    coursePk1 = found.id;
                }
            }

            if (!coursePk1) {
                return; // error already captured
            }

            const credits =
                r.credits !== undefined &&
                r.credits !== null &&
                String(r.credits).trim() !== "" &&
                !Number.isNaN(Number(r.credits))
                    ? String(r.credits)
                    : "";

            const courseObj = normalizedCourse
                ? {
                    id: String(normalizedCourse.id),
                    courseId: normalizedCourse.courseId,
                    name: normalizedCourse.name,
                    availability: normalizedCourse.availability,
                    enrollUrl: r.enrollUrl || "",
                    credits,
                }
                : {
                    id: String(coursePk1),
                    courseId: r.courseId || "",
                    name: "",
                    availability: "",
                    enrollUrl: r.enrollUrl || "",
                    credits,
                };

            const step = steps[stepIndex];
            const existingIdx = (step.courses || []).findIndex(
                (c) => String(c.id) === String(courseObj.id)
            );

            if (existingIdx >= 0) {
                step.courses[existingIdx] = {
                    ...step.courses[existingIdx],
                    ...courseObj,
                };
            } else {
                step.courses.push(courseObj);
            }
            addedCourses += 1;
        });

        // apply student rows
        studentRows.forEach((r) => {
            const rowLabel = `row ${r.rowNum}`;
            let userPk1 = (r.userPk1 || "").trim();
            let userName = (r.userName || "").trim();
            let normalizedUser = null;

            const userNameLower = userName ? userName.toLowerCase() : "";

            if (!userPk1 && userNameLower) {
                const found = userLookup.get(userNameLower);
                if (found) {
                    normalizedUser = found;
                    userPk1 = found.id;
                    userName = found.userName || userName;
                }
            }

            if (!userPk1) {
                errors.push(
                    `${rowLabel}: could not resolve user from userName/userPk1.`
                );
                return;
            }

            const enrollmentObj = normalizedUser
                ? {
                    id: String(normalizedUser.id),
                    userName: normalizedUser.userName || userName || "",
                    name: normalizedUser.name || "",
                }
                : {
                    id: String(userPk1),
                    userName: userName || "",
                    name: "",
                };

            const already = enrollments.some(
                (e) =>
                    e.id === enrollmentObj.id ||
                    (enrollmentObj.userName &&
                        e.userName === enrollmentObj.userName)
            );
            if (!already) {
                enrollments.push(enrollmentObj);
                addedStudents += 1;
            }
        });

        const updatedPathway = {
            ...base,
            steps,
            enrollments,
        };

        setCurrentPathway(updatedPathway);
        setDirty(true);

        setCsvInfo(M.csv.applied(addedCourses, addedStudents));
        if (errors.length) {
            setCsvError(errors.join(" "));
        } else {
            setCsvError("");
        }
    }

    function handleCsvFileChange(e) {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        setCsvBusy(true);
        setCsvError("");
        setCsvInfo("");

        const reader = new FileReader();
        reader.onload = async (ev) => {
            try {
                const text = String(ev.target?.result || "");
                await applyCsvText(text);
            } catch (err) {
                console.error("[PathwaysAdmin] CSV upload error", err);
                setCsvError(err?.message || "Failed to process CSV file.");
            } finally {
                setCsvBusy(false);
                // reset input so the same file can be chosen again if needed
                e.target.value = "";
            }
        };
        reader.onerror = () => {
            setCsvBusy(false);
            setCsvError("Failed to read CSV file.");
        };
        reader.readAsText(file);
    }

    /** ---------------- Gates ---------------- */
    if (!roleChecked) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.gate.checking}</div>
            </div>
        );
    }

    if (!hasAdminRole) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div
                    className="bb-card"
                    style={{
                        padding: 10,
                        border: "1px solid #ddd",
                        borderRadius: 8,
                    }}
                >
                    <h2 style={{ margin: 0 }}>{M.gate.blockedTitle}</h2>
                    <p style={{ fontSize: 12 }}>{M.gate.blockedMsg}</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.notices.loading}</div>
            </div>
        );
    }

    if (fatalError) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div
                    className="bb-card"
                    style={{
                        padding: 10,
                        border: "1px solid #ddd",
                        borderRadius: 8,
                    }}
                >
                    <h2 style={{ margin: 0 }}>{M.headings.error}</h2>
                    <p style={{ fontSize: 12 }}>{fatalError}</p>
                </div>
            </div>
        );
    }

    /** ---------------- Main UI ---------------- */
    const activeRequirement =
        currentPathway &&
        (currentPathway.steps || []).find(
            (s) => s.id === activeStepId
        );

    return (
        <div className="bb-container" style={{ display: "grid", gap: 12 }}>
            <style dangerouslySetInnerHTML={{ __html: compactCss }} />

            {/* Pathways list */}
            <div
                className="bb-card"
                style={{
                    padding: 10,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                }}
            >
                <div
                    className="bb-row"
                    style={{ justifyContent: "space-between", marginBottom: 4 }}
                >
                    <h2 style={{ margin: 0 }}>{M.headings.list}</h2>
                    <div className="bb-row">
                        <button
                            type="button"
                            className="bb-button secondary"
                            onClick={() =>
                                loadPathways({
                                    clearMessages: true,
                                    suppressSpinner: false,
                                })
                            }
                        >
                            {M.buttons.refreshList}
                        </button>
                        <button
                            type="button"
                            className="bb-button"
                            style={{ marginLeft: 6 }}
                            onClick={startNewPathway}
                        >
                            {M.buttons.newPathway}
                        </button>
                    </div>
                </div>

                <table className="bb-table" role="grid">
                    <thead>
                    <tr>
                        <th>{M.table.pathwayId}</th>
                        <th>{M.table.pathwayLabel}</th>
                        <th>{M.table.steps}</th>
                        <th>{M.table.updatedAt}</th>
                        <th style={{ width: 210 }}>{M.table.actions}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {pathways.length === 0 ? (
                        <tr>
                            <td colSpan={5} style={{ textAlign: "center" }}>
                                {M.table.noResults}
                            </td>
                        </tr>
                    ) : (
                        pathways.map((p, idx) => (
                            <tr key={p.pathwayId || `p_${idx}`}>
                                <td>{p.pathwayId || "-"}</td>
                                <td>{p.label || "-"}</td>
                                <td>{(p.steps || []).length}</td>
                                <td>
                                    {p.updatedAt
                                        ? String(p.updatedAt).slice(0, 19)
                                        : "-"}
                                </td>
                                <td>
                                    <div className="bb-row">
                                        <button
                                            type="button"
                                            className="bb-button secondary"
                                            onClick={() =>
                                                viewPathway(p.pathwayId)
                                            }
                                        >
                                            {M.buttons.view}
                                        </button>
                                        <button
                                            type="button"
                                            className="bb-button"
                                            style={{ marginLeft: 4 }}
                                            onClick={() =>
                                                editExistingPathway(
                                                    p.pathwayId
                                                )
                                            }
                                        >
                                            {M.buttons.edit}
                                        </button>
                                        <button
                                            type="button"
                                            className="bb-button danger"
                                            style={{ marginLeft: 4 }}
                                            onClick={() =>
                                                deletePathway(p.pathwayId)
                                            }
                                        >
                                            {M.buttons.delete}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pathway editor */}
            <div
                className="bb-card"
                style={{
                    padding: 10,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                }}
            >
                <div
                    className="bb-row"
                    style={{ justifyContent: "space-between", marginBottom: 4 }}
                >
                    <h2 style={{ margin: 0 }}>{M.headings.main}</h2>
                    <button
                        className="bb-button"
                        onClick={saveCurrentPathway}
                        disabled={
                            !currentPathway || !dirty || saving || viewMode
                        }
                        aria-disabled={
                            !currentPathway || !dirty || saving || viewMode
                        }
                        title={M.buttons.savePathway}
                    >
                        {viewMode
                            ? M.buttons.saved
                            : dirty
                                ? saving
                                    ? M.buttons.searching
                                    : M.buttons.savePathway
                                : M.buttons.saved}
                    </button>
                </div>

                {viewMode && currentPathway && (
                    <div
                        style={{
                            marginBottom: 6,
                            padding: "6px 8px",
                            borderRadius: 6,
                            border: "1px dashed #c7d6ff",
                            background: "#f4f6ff",
                            fontSize: 11,
                            color: "#31427a",
                        }}
                    >
                        {M.viewModeBanner}
                    </div>
                )}

                <Flash type="error">{inlineError}</Flash>
                <Flash type="success">{inlineSuccess}</Flash>

                {!currentPathway ? (
                    <p style={{ fontSize: 12, marginTop: 4 }}>
                        {M.pathway.noPathwaySelected}
                    </p>
                ) : (
                    <>
                        {/* Pathway label */}
                        <div
                            style={{
                                marginTop: 8,
                                marginBottom: 8,
                                padding: 8,
                                borderRadius: 6,
                                border: "1px solid #eee",
                            }}
                        >
                            <label
                                style={{
                                    display: "block",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    marginBottom: 4,
                                }}
                            >
                                {M.pathway.label}
                            </label>
                            <input
                                type="text"
                                value={currentPathway.label || ""}
                                onChange={(e) => updateLabel(e.target.value)}
                                placeholder={M.pathway.labelPlaceholder}
                                style={{ width: "100%" }}
                                disabled={viewMode}
                            />
                        </div>

                        {/* Requirements */}
                        <div
                            style={{
                                marginTop: 0,
                                padding: 8,
                                borderRadius: 6,
                                border: "1px solid #eee",
                            }}
                        >
                            <div
                                className="bb-row"
                                style={{ justifyContent: "space-between" }}
                            >
                                <h3
                                    style={{
                                        margin: 0,
                                        fontSize: 14,
                                        fontWeight: 600,
                                    }}
                                >
                                    {M.headings.requirements}
                                </h3>
                                <button
                                    type="button"
                                    className="bb-button"
                                    onClick={addRequirementStep}
                                    disabled={viewMode}
                                >
                                    {M.buttons.addRequirement}
                                </button>
                            </div>

                            {!(currentPathway.steps || []).length ? (
                                <p style={{ fontSize: 12, marginTop: 8 }}>
                                    {M.pathway.noRequirements}
                                </p>
                            ) : (
                                <div
                                    style={{
                                        marginTop: 8,
                                        display: "grid",
                                        gap: 8,
                                    }}
                                >
                                    {/* Active requirement selector */}
                                    <div
                                        className="bb-row"
                                        style={{ alignItems: "center" }}
                                    >
                                        <span style={{ fontSize: 12 }}>
                                            {M.pathway.activeRequirement}
                                        </span>
                                        <select
                                            className="bb-select"
                                            value={activeStepId || ""}
                                            onChange={(e) =>
                                                setActiveStepId(
                                                    e.target.value || null
                                                )
                                            }
                                            disabled={viewMode}
                                        >
                                            {(currentPathway.steps || []).map(
                                                (step, index) => (
                                                    <option
                                                        key={step.id}
                                                        value={step.id}
                                                    >
                                                        {step.title ||
                                                            M.pathway.requirementLabel(
                                                                index
                                                            )}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>

                                    {(currentPathway.steps || []).map(
                                        (step, index) => {
                                            const isActive =
                                                step.id === activeStepId;
                                            return (
                                                <div
                                                    key={step.id}
                                                    style={{
                                                        borderRadius: 6,
                                                        border:
                                                            "1px solid #ddd",
                                                        padding: 8,
                                                        background: isActive
                                                            ? "#f9fbff"
                                                            : "#fafafa",
                                                    }}
                                                >
                                                    <div
                                                        className="bb-row"
                                                        style={{
                                                            justifyContent:
                                                                "space-between",
                                                            marginBottom: 6,
                                                        }}
                                                    >
                                                        <div
                                                            className="bb-row"
                                                            style={{ flex: 1 }}
                                                        >
                                                            <span
                                                                style={{
                                                                    fontSize: 12,
                                                                    marginRight: 4,
                                                                }}
                                                            >
                                                                {M.pathway.requirementLabel(
                                                                    index
                                                                )}
                                                                :
                                                            </span>
                                                            <input
                                                                type="text"
                                                                value={
                                                                    step.title ||
                                                                    ""
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) =>
                                                                    updateStepTitle(
                                                                        step.id,
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                placeholder="Optional label (e.g., 'Intro level courses')"
                                                                style={{
                                                                    flex: 1,
                                                                    minWidth: 200,
                                                                }}
                                                                disabled={
                                                                    viewMode
                                                                }
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="bb-button danger"
                                                            onClick={() =>
                                                                removeRequirementStep(
                                                                    step.id
                                                                )
                                                            }
                                                            disabled={viewMode}
                                                        >
                                                            {
                                                                M.buttons
                                                                    .removeRequirement
                                                            }
                                                        </button>
                                                    </div>

                                                    <div
                                                        className="bb-row"
                                                        style={{
                                                            marginBottom: 6,
                                                            alignItems:
                                                                "center",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            {
                                                                M.pathway
                                                                    .requirementRule
                                                            }
                                                        </span>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            value={
                                                                step.minRequired
                                                            }
                                                            onChange={(e) =>
                                                                updateStepMinRequired(
                                                                    step.id,
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            style={{
                                                                width: 60,
                                                                textAlign:
                                                                    "right",
                                                            }}
                                                            disabled={viewMode}
                                                        />
                                                        <span
                                                            style={{
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            {
                                                                M.pathway
                                                                    .ofFollowing
                                                            }
                                                        </span>
                                                    </div>

                                                    <div>
                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                marginBottom: 4,
                                                            }}
                                                        >
                                                            {
                                                                M.pathway
                                                                    .coursesHeader
                                                            }
                                                        </div>
                                                        {step.courses &&
                                                        step.courses.length >
                                                        0 ? (
                                                            <table
                                                                className="bb-table"
                                                                role="grid"
                                                            >
                                                                <thead>
                                                                <tr>
                                                                    <th>
                                                                        {
                                                                            M
                                                                                .table
                                                                                .name
                                                                        }
                                                                    </th>
                                                                    <th>
                                                                        {
                                                                            M
                                                                                .table
                                                                                .id
                                                                        }
                                                                    </th>
                                                                    <th>
                                                                        {
                                                                            M
                                                                                .table
                                                                                .credits
                                                                        }
                                                                    </th>
                                                                    <th>
                                                                        {
                                                                            M
                                                                                .table
                                                                                .enrollLink
                                                                        }
                                                                    </th>
                                                                    <th
                                                                        style={{
                                                                            width: 90,
                                                                        }}
                                                                    >
                                                                        {
                                                                            M
                                                                                .table
                                                                                .actions
                                                                        }
                                                                    </th>
                                                                </tr>
                                                                </thead>
                                                                <tbody>
                                                                {step.courses.map(
                                                                    (c) => (
                                                                        <tr
                                                                            key={
                                                                                c.id
                                                                            }
                                                                        >
                                                                            <td>
                                                                                {c.name ||
                                                                                    "-"}
                                                                            </td>
                                                                            <td>
                                                                                {c.courseId ||
                                                                                    "-"}
                                                                            </td>
                                                                            <td>
                                                                                {viewMode ? (
                                                                                    c.credits &&
                                                                                    String(
                                                                                        c.credits
                                                                                    ).trim() !==
                                                                                    "" ? (
                                                                                        String(
                                                                                            c.credits
                                                                                        )
                                                                                    ) : (
                                                                                        "-"
                                                                                    )
                                                                                ) : (
                                                                                    <input
                                                                                        type="number"
                                                                                        min={0}
                                                                                        step="0.5"
                                                                                        value={
                                                                                            c.credits ??
                                                                                            ""
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            updateCourseCredits(
                                                                                                step.id,
                                                                                                c.id,
                                                                                                e
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                        }
                                                                                        placeholder="Credits"
                                                                                        style={{
                                                                                            width:
                                                                                                "100%",
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </td>
                                                                            <td>
                                                                                {viewMode ? (
                                                                                    c.enrollUrl ? (
                                                                                        <a
                                                                                            href={
                                                                                                c.enrollUrl
                                                                                            }
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            style={{
                                                                                                fontSize:
                                                                                                    12,
                                                                                            }}
                                                                                        >
                                                                                            {c.enrollUrl}
                                                                                        </a>
                                                                                    ) : (
                                                                                        "-"
                                                                                    )
                                                                                ) : (
                                                                                    <input
                                                                                        type="text"
                                                                                        value={
                                                                                            c.enrollUrl ||
                                                                                            ""
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            updateCourseEnrollUrl(
                                                                                                step.id,
                                                                                                c.id,
                                                                                                e
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                        }
                                                                                        placeholder="Optional enroll URL"
                                                                                        style={{
                                                                                            width:
                                                                                                "100%",
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </td>
                                                                            <td>
                                                                                <button
                                                                                    type="button"
                                                                                    className="bb-button danger"
                                                                                    onClick={() =>
                                                                                        removeCourseFromStep(
                                                                                            step.id,
                                                                                            c.id
                                                                                        )
                                                                                    }
                                                                                    disabled={
                                                                                        viewMode
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        M
                                                                                            .buttons
                                                                                            .removeCourse
                                                                                    }
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                )}
                                                                </tbody>
                                                            </table>
                                                        ) : (
                                                            <p
                                                                style={{
                                                                    fontSize: 12,
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                {
                                                                    M.pathway
                                                                        .noCoursesInRequirement
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Enrollments */}
                        <div
                            style={{
                                marginTop: 8,
                                padding: 8,
                                borderRadius: 6,
                                border: "1px solid #eee",
                            }}
                        >
                            <h3
                                style={{
                                    margin: 0,
                                    fontSize: 14,
                                    fontWeight: 600,
                                }}
                            >
                                {M.headings.enrollments}
                            </h3>
                            <p
                                style={{
                                    fontSize: 12,
                                    marginTop: 2,
                                    marginBottom: 6,
                                }}
                            >
                                {M.headings.enrollmentsSub}
                            </p>

                            {!(currentPathway.enrollments || []).length ? (
                                <p style={{ fontSize: 12, margin: 0 }}>
                                    {M.pathway.noEnrollments}
                                </p>
                            ) : (
                                <table
                                    className="bb-table"
                                    role="grid"
                                    style={{ marginTop: 4 }}
                                >
                                    <thead>
                                    <tr>
                                        <th>{M.table.name}</th>
                                        <th>{M.table.username}</th>
                                        <th style={{ width: 90 }}>
                                            {M.table.actions}
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(currentPathway.enrollments || []).map(
                                        (e, idx) => (
                                            <tr
                                                key={
                                                    e.id ||
                                                    e.userName ||
                                                    `enr_${idx}`
                                                }
                                            >
                                                <td>
                                                    {e.name && e.name.trim()
                                                        ? e.name
                                                        : "-"}
                                                </td>
                                                <td>
                                                    {e.userName || "-"}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="bb-button danger"
                                                        onClick={() =>
                                                            removeEnrollment(
                                                                e.id ||
                                                                e.userName
                                                            )
                                                        }
                                                        disabled={viewMode}
                                                    >
                                                        {
                                                            M.buttons
                                                                .removeEnrollment
                                                        }
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Bulk CSV upload */}
            <div
                className="bb-card"
                style={{
                    padding: 10,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                }}
            >
                <h2 style={{ margin: 0 }}>{M.csv.heading}</h2>
                <p style={{ fontSize: 12, marginTop: 4, marginBottom: 6 }}>
                    {M.csv.help}
                </p>
                <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleCsvFileChange}
                    disabled={!currentPathway || viewMode || csvBusy}
                />
                {csvBusy && (
                    <p style={{ fontSize: 12, marginTop: 4 }}>Processing CSV…</p>
                )}
                {csvInfo && (
                    <p
                        style={{
                            fontSize: 12,
                            marginTop: 4,
                            color: "#005c2e",
                        }}
                    >
                        {csvInfo}
                    </p>
                )}
                {csvError && (
                    <p
                        style={{
                            fontSize: 12,
                            marginTop: 4,
                            color: "#a40000",
                        }}
                    >
                        {csvError}
                    </p>
                )}
                <p
                    style={{
                        fontSize: 11,
                        marginTop: 6,
                        color: "#555",
                    }}
                >
                    Example rows:
                    <br />
                    <code>
                        type,requirement,courseId,credits,enrollUrl
                        <br />
                        course,1,QE_TEST_001U,3,https://enroll.example.edu/QE_TEST_001U
                    </code>
                    <br />
                    <code>
                        type,userName
                        <br />
                        student,jstudent1
                    </code>
                </p>
            </div>

            {/* User search (for enrollments) */}
            <div
                className="bb-card"
                style={{
                    padding: 10,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                }}
            >
                <div
                    className="bb-row"
                    style={{ justifyContent: "space-between" }}
                >
                    <h2 style={{ margin: 0 }}>{M.headings.userSearch}</h2>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        doUserSearch();
                    }}
                    style={{ marginTop: 8, marginBottom: 6 }}
                    noValidate
                >
                    <div className="bb-row">
                        <input
                            type="text"
                            placeholder={M.userSearch.placeholder}
                            value={userSearchTerm}
                            onChange={(e) => setUserSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    doUserSearch();
                                }
                            }}
                            autoComplete="off"
                            spellCheck={false}
                            style={{ minWidth: 320 }}
                        />
                        <button
                            type="submit"
                            className="bb-button"
                            disabled={userSearchLoading}
                            aria-busy={userSearchLoading}
                        >
                            {userSearchLoading
                                ? M.buttons.searching
                                : M.buttons.search}
                        </button>
                        <button
                            type="button"
                            className="bb-button secondary"
                            onClick={() => {
                                setUserSearchTerm("");
                                setUserSearchResults([]);
                                setUserSearchError("");
                            }}
                            title={M.buttons.clear}
                        >
                            {M.buttons.clear}
                        </button>
                        {userSearchError && (
                            <span
                                style={{
                                    color: "#a40000",
                                    fontSize: 12,
                                    marginLeft: 4,
                                }}
                            >
                                {userSearchError}
                            </span>
                        )}
                    </div>
                </form>

                <table className="bb-table" role="grid">
                    <thead>
                    <tr>
                        <th>{M.table.name}</th>
                        <th>{M.table.username}</th>
                        <th style={{ width: 160 }}>{M.table.actions}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {userSearchResults.length === 0 ? (
                        <tr>
                            <td
                                colSpan={3}
                                style={{ textAlign: "center" }}
                            >
                                {userSearchLoading
                                    ? M.buttons.searching
                                    : M.table.noResults}
                            </td>
                        </tr>
                    ) : (
                        userSearchResults.map((u) => {
                            const isEnrolled =
                                !!currentPathway &&
                                (currentPathway.enrollments || []).some(
                                    (e) =>
                                        e.id === u.id ||
                                        e.userName === u.userName
                                );
                            return (
                                <tr key={u.id || u.userName}>
                                    <td>{u.name || "-"}</td>
                                    <td>{u.userName || "-"}</td>
                                    <td>
                                        <div className="bb-row">
                                            <button
                                                type="button"
                                                className="bb-button secondary"
                                                onClick={() =>
                                                    addStudentToPathway(u)
                                                }
                                                disabled={
                                                    !currentPathway ||
                                                    viewMode
                                                }
                                                aria-disabled={
                                                    !currentPathway ||
                                                    viewMode
                                                }
                                                title={
                                                    M.buttons
                                                        .addEnrollment
                                                }
                                            >
                                                {isEnrolled
                                                    ? "Already enrolled"
                                                    : M.buttons
                                                        .addEnrollment}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                    </tbody>
                </table>
            </div>

            {/* Course search */}
            <div
                className="bb-card"
                style={{
                    padding: 10,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                }}
            >
                <div
                    className="bb-row"
                    style={{ justifyContent: "space-between" }}
                >
                    <h2 style={{ margin: 0 }}>{M.headings.search}</h2>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        doSearch();
                    }}
                    style={{ marginTop: 8, marginBottom: 6 }}
                    noValidate
                >
                    <div className="bb-row">
                        <input
                            type="text"
                            placeholder={M.search.placeholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    doSearch();
                                }
                            }}
                            autoComplete="off"
                            spellCheck={false}
                            style={{ minWidth: 320 }}
                        />
                        <button
                            type="submit"
                            className="bb-button"
                            disabled={searchLoading}
                            aria-busy={searchLoading}
                        >
                            {searchLoading
                                ? M.buttons.searching
                                : M.buttons.search}
                        </button>
                        <button
                            type="button"
                            className="bb-button secondary"
                            onClick={() => {
                                setSearchTerm("");
                                setSearchResults([]);
                                setSearchError("");
                            }}
                            title={M.buttons.clear}
                        >
                            {M.buttons.clear}
                        </button>
                        {searchError && (
                            <span
                                style={{
                                    color: "#a40000",
                                    fontSize: 12,
                                    marginLeft: 4,
                                }}
                            >
                                {searchError}
                            </span>
                        )}
                    </div>
                </form>

                <table className="bb-table" role="grid">
                    <thead>
                    <tr>
                        <th>{M.table.name}</th>
                        <th>{M.table.id}</th>
                        <th>{M.table.availability}</th>
                        <th style={{ width: 160 }}>{M.table.actions}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {searchResults.length === 0 ? (
                        <tr>
                            <td
                                colSpan={4}
                                style={{ textAlign: "center" }}
                            >
                                {searchLoading
                                    ? M.buttons.searching
                                    : M.table.noResults}
                            </td>
                        </tr>
                    ) : (
                        searchResults.map((c) => {
                            const isInActive =
                                !!activeRequirement &&
                                (activeRequirement.courses || []).some(
                                    (crs) => crs.id === c.id
                                );
                            return (
                                <tr key={c.id}>
                                    <td>{c.name}</td>
                                    <td>{c.courseId || "-"}</td>
                                    <td>
                                        {String(c.availability || "-")}
                                    </td>
                                    <td>
                                        <div className="bb-row">
                                            <button
                                                type="button"
                                                className="bb-button secondary"
                                                onClick={() =>
                                                    addCourseToActiveRequirement(
                                                        c
                                                    )
                                                }
                                                style={{ marginLeft: 0 }}
                                                disabled={
                                                    !activeRequirement ||
                                                    viewMode
                                                }
                                                aria-disabled={
                                                    !activeRequirement ||
                                                    viewMode
                                                }
                                                title={
                                                    M.buttons
                                                        .addToRequirement
                                                }
                                            >
                                                {isInActive
                                                    ? "Already in requirement"
                                                    : M.buttons
                                                        .addToRequirement}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PathwaysAdmin;